import React, { useState } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Alert,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip
} from '@mui/material';
import {
  FolderOpen as FolderIcon,
  Timer as TimerIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Stop as StopIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Info as InfoIcon,
  Business as DepartmentIcon
} from '@mui/icons-material';
import { ScanResult, ScanError } from '../types';

interface ScannerStatusProps {
  isScanning: boolean;
  scanProgress: number;
  scanResults: ScanResult | null;
  currentPath?: string;
  onCancel?: () => void;
  error?: string;
}

const ScannerStatus: React.FC<ScannerStatusProps> = ({
  isScanning,
  scanProgress,
  scanResults,
  currentPath,
  onCancel,
  error
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const statistics = scanResults?.statistics;
  const scanErrors = scanResults?.errors ?? [];
  const consultantEntries = Object.entries(statistics?.byConsultantType ?? {});
  const departmentEntries = Object.entries(statistics?.byDepartment ?? {});
  const departmentOverflow = Math.max(departmentEntries.length - 5, 0);
  const scanStartedAt =
    scanResults?.scanStarted instanceof Date
      ? scanResults.scanStarted
      : scanResults?.scanStarted
        ? new Date(scanResults.scanStarted)
        : undefined;

  const formatDuration = (startTime: Date, endTime?: Date) => {
    const end = endTime || new Date();
    const duration = end.getTime() - startTime.getTime();
    const seconds = Math.floor(duration / 1000);
    const minutes = Math.floor(seconds / 60);

    if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    }
    return `${seconds}s`;
  };

  const getScanStatusColor = () => {
    if (error) return 'error';
    if (isScanning) return 'primary';
    if (scanResults) return 'success';
    return 'grey';
  };

  const getScanStatusText = () => {
    if (error) return 'Scan Failed';
    if (isScanning) return 'Scanning in Progress';
    if (scanResults) return 'Scan Completed';
    return 'Ready to Scan';
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        SharePoint Scan Status
      </Typography>

      <Grid container spacing={3}>
        {/* Main Status Card */}
        <Grid size={12}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {getScanStatusText()}
              </Typography>
              <Chip
                label={getScanStatusText()}
                color={getScanStatusColor() as any}
                variant={isScanning ? 'filled' : 'outlined'}
                icon={isScanning ? <TimerIcon /> : scanResults ? <CheckIcon /> : <FolderIcon />}
              />
            </Box>

            {/* Error Display */}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  {error}
                </Typography>
              </Alert>
            )}

            {/* Progress Display */}
            {isScanning && (
              <Box sx={{ mb: 3 }}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" color="text.secondary">
                    {currentPath || 'Initializing scan...'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {Math.round(scanProgress)}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={scanProgress}
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Box display="flex" justifyContent="space-between" mt={2}>
                  <Typography variant="body2" color="text.secondary">
                    Scanning SharePoint folders...
                  </Typography>
                  {onCancel && (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<StopIcon />}
                      onClick={onCancel}
                    >
                      Cancel Scan
                    </Button>
                  )}
                </Box>
              </Box>
            )}

            {/* Scan Results Summary */}
            {scanResults && (
              <Box>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Card variant="outlined">
                      <CardContent sx={{ textAlign: 'center', p: 2 }}>
                        <Typography variant="h4" color="primary">
                          {scanResults.totalFolders}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Folders
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Card variant="outlined">
                      <CardContent sx={{ textAlign: 'center', p: 2 }}>
                        <Typography variant="h4" color="success.main">
                          {statistics?.withDocuments ?? 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          With Documents
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Card variant="outlined">
                      <CardContent sx={{ textAlign: 'center', p: 2 }}>
                        <Typography variant="h4" color="warning.main">
                          {statistics?.withoutDocuments ?? 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Empty Folders
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Card variant="outlined">
                      <CardContent sx={{ textAlign: 'center', p: 2 }}>
                        <Typography variant="h4" color="error.main">
                          {scanErrors.length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Errors
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                <Box display="flex" gap={2} mt={3}>
                  <Button
                    variant="outlined"
                    startIcon={showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    onClick={() => setShowDetails(!showDetails)}
                  >
                    {showDetails ? 'Hide' : 'Show'} Details
                  </Button>
                  {scanErrors.length > 0 && (
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<ErrorIcon />}
                      onClick={() => setShowErrors(!showErrors)}
                    >
                      {showErrors ? 'Hide' : 'Show'} Errors ({scanErrors.length})
                    </Button>
                  )}
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Detailed Breakdown */}
        {scanResults && (
          <Grid size={12}>
            <Collapse in={showDetails}>
              <Paper elevation={1} sx={{ p: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  Scan Breakdown
                </Typography>

                <Grid container spacing={3}>
                  {/* By Consultant Type */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      <DepartmentIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                      By Consultant Type
                    </Typography>
                    <List dense>
                      {consultantEntries.map(([type, count]) => (
                        <ListItem key={type}>
                          <ListItemIcon>
                            <FolderIcon color="action" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={type}
                            secondary={`${count} folders`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>

                  {/* By Department */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      <DepartmentIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                      By Department
                    </Typography>
                    <List dense>
                      {departmentEntries.slice(0, 5).map(([dept, count]) => (
                        <ListItem key={dept}>
                          <ListItemIcon>
                            <FolderIcon color="action" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={dept}
                            secondary={`${count} folders`}
                          />
                        </ListItem>
                      ))}
                      {departmentOverflow > 0 && (
                        <ListItem>
                          <ListItemText
                            primary={`... and ${departmentOverflow} more`}
                            sx={{ fontStyle: 'italic', color: 'text.secondary' }}
                          />
                        </ListItem>
                      )}
                    </List>
                  </Grid>
                </Grid>

                {scanStartedAt && (
                  <Box mt={2}>
                    <Typography variant="body2" color="text.secondary">
                      <InfoIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: 'small' }} />
                      Scan completed in {formatDuration(scanStartedAt, scanResults?.scanCompleted ? new Date() : undefined)}
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Collapse>
          </Grid>
        )}

        {/* Error Details */}
        {scanResults && scanErrors.length > 0 && (
          <Grid size={12}>
            <Collapse in={showErrors}>
              <Paper elevation={1} sx={{ p: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }} color="error">
                  <ErrorIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                  Scan Errors
                </Typography>

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Path</TableCell>
                        <TableCell>Error Type</TableCell>
                        <TableCell>Message</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {scanErrors.slice(0, 10).map((error: ScanError, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {error.path}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={error.type}
                              size="small"
                              color={error.type === 'access_denied' ? 'error' : 'warning'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Tooltip title={error.message}>
                              <Typography variant="body2" sx={{
                                maxWidth: 300,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {error.message}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {scanErrors.length > 10 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Showing first 10 of {scanErrors.length} errors
                  </Typography>
                )}
              </Paper>
            </Collapse>
          </Grid>
        )}

        {/* Information */}
        {!isScanning && !scanResults && !error && (
          <Grid size={12}>
            <Alert severity="info">
              <Typography variant="body2">
                The scan will discover all employee folders in the SharePoint Professional Profile library,
                excluding system folders and administrative directories. This process typically takes 1-3 minutes
                depending on the number of folders.
              </Typography>
            </Alert>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default ScannerStatus;
